/* eslint-disable multiline-ternary */
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { type Channel } from 'itty-sockets'
import {
  Box,
  Heading,
  Spinner,
  Flex,
  Text,
  FormControl,
  FormLabel,
  InputGroup,
  Input,
  Button,
  Link,
  Img,
  FormHelperText
} from '@chakra-ui/react'
import { styles } from './styles'
import { type Creator } from '../playalong/types'

export enum PAStates {
  WAITING,
  GUESSING,
  RESULTS,
  END
}

interface PlayAlongProps {
  channel: Channel
  playerName: string
}

const PlayAlong = ({ channel, playerName }: PlayAlongProps): ReactElement => {
  const [phase, setPhase] = useState<PAStates>(PAStates.WAITING)
  const [guess, setGuess] = useState<string>('')
  const [prompt, setPrompt] = useState<string>('')
  const [promptType, setPromptType] = useState<string>('')
  const [creator, setCreator] = useState<Creator>()
  const [choices, setChoices] = useState<string[] | null>([])
  const [lastSentGuess, setLastSentGuess] = useState<string>('')
  const [selectedChoice, setSelectedChoice] = useState<number>()
  const [scores, setScores] = useState<any[]>([])
  const [hideNav, setHideNav] = useState(false)

  const sendGuess = (): void => {
    if (channel != null) {
      setLastSentGuess(guess)

      channel.send({
        id: channel.myID,
        clientId: channel.myAlias,
        type: 'guess',
        guess: '!guess ' + guess
      })
      setGuess('')
    }
  }

  const triggerAction = (message: any): void => {
    if (message.creator !== undefined) {
      setCreator(message.creator)
    }
    switch (message.action) {
      case 'guessing':
        setPrompt(message.value)
        setPromptType(message.type)
        setChoices(message.choices)
        setPhase(PAStates.GUESSING)
        break
      case 'waiting':
        setPrompt('')
        setScores(message.value)
        setPhase(PAStates.WAITING)
        break
      case 'score':
        setScores(message.value)
        break
      case 'end':
        setPrompt('')
        setScores(message.value)
        setPhase(PAStates.END)
        break
      default:
    }
  }

  useEffect(() => {
    switch (phase) {
      case PAStates.GUESSING:
        setGuess('')
        setSelectedChoice(undefined)
        setLastSentGuess('')
        break
      default:
    }
  }, [phase])

  useEffect(() => {
    if (choices !== null && selectedChoice !== undefined) {
      sendGuess()
    }
  }, [selectedChoice])

  useEffect(() => {
    channel?.on('message', (message: any) => {
      triggerAction(message)
    })
    const params = new URLSearchParams(window.location.search)
    if (params.get('mobile-no-nav') === 'true') {
      setHideNav(true)
    }
  }, [])

  const yourScore = useMemo(
    () => scores.find((score) => score.username === playerName)?.score ?? 0,
    [scores, playerName]
  )

  return (
    <Flex
      sx={{
        ...styles.container,
        height: hideNav ? 'calc(100dvh)' : styles.container.height
      }}
      gap="20px"
    >
      {hideNav ? (
        <style
          dangerouslySetInnerHTML={{
            __html: `header { display: none; }
              #content { min-height: 100vh !important;}`
          }}
        />
      ) : null}
      {/* Waiting */}
      <Flex
        flexDir="column"
        display={phase === PAStates.WAITING ? 'flex' : 'none'}
        alignItems="center"
        h="100%"
      >
        <Flex
          flexDir="column"
          align="center"
          flexGrow={1}
          justifyContent="center"
        >
          <Heading
            fontWeight={800}
            letterSpacing={'-0.05em'}
            textAlign="center"
            mb="20px"
          >
            Your Score: {yourScore}
          </Heading>
          <Box>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
          </Box>
          <Heading
            fontWeight={800}
            letterSpacing={'-0.05em'}
            textAlign="center"
            sx={{
              textWrap: 'balance'
            }}
            mb="20px"
          >
            Watch the screen for the next prompt
          </Heading>
        </Flex>
        {creator !== undefined ? (
          <Link target="_blank" href={creator?.qrCode}>
            <Flex direction="row" align="center" gap="5px">
              <Img
                src={creator?.channelIcon}
                w="50vw"
                h="50vw"
                maxW="75px"
                maxH="75px"
                borderRadius="50px"
                boxShadow={'0px 0px 3px 2px yellow.400'}
                alt={creator?.name}
                mb="10px"
              />
              <Text
                display="inline-block"
                w="auto"
                mx="0"
                fontSize="22px"
                sx={{
                  textWrap: 'balance'
                }}
              >
                Click Here to Learn More About&nbsp;{creator?.name}
              </Text>
            </Flex>
          </Link>
        ) : null}
      </Flex>
      {/* End Waiting */}
      {/* Guess */}
      <Flex
        pos="relative"
        alignItems="center"
        flexDir="column"
        justifyContent="space-evenly"
        h="100%"
        display={phase === PAStates.GUESSING ? 'flex' : 'none'}
      >
        <Text textAlign="center"></Text>
        <Heading
          fontWeight={800}
          letterSpacing={'-0.05em'}
          textAlign="center"
          mb="10px"
          fontSize="24px"
        >
          {prompt}
        </Heading>
        {choices !== null ? (
          <Box>
            <Text textAlign="center">
              Select Your Guess Below
              <br />
              (You Can Change It While The Prompt Is On Screen)
            </Text>
            <Flex gap="5px" flexDir="column">
              {choices.map((choice, index) => (
                <Flex
                  as="button"
                  w="100%"
                  key={index}
                  flexDir="row"
                  gap="10px"
                  justifyContent="space-between"
                  overflow="hidden"
                  border="2px solid"
                  borderColor="gray.700"
                  bg="gray.700"
                  opacity={
                    selectedChoice === index || selectedChoice === undefined
                      ? 1
                      : 0.5
                  }
                  textAlign="left"
                  color="yellow.300"
                  fontWeight={600}
                  borderRadius="30px"
                  p="10px 20px"
                  onClick={() => {
                    setGuess(String.fromCharCode(97 + index))
                    setSelectedChoice(index)
                  }}
                >
                  <Box>
                    {String.fromCharCode(97 + index).toUpperCase()}
                    {'. '}
                    {choice}
                  </Box>
                </Flex>
              ))}
            </Flex>
          </Box>
        ) : (
          <>
            {lastSentGuess !== '' && (
              <Heading
                fontWeight={800}
                letterSpacing={'-0.05em'}
                textAlign="center"
                mt="20px"
                mb="10px"
                fontSize="24px"
              >
                Your Guess Was...
                <br />
                {lastSentGuess}
                <br />
                <sub>(You still have time to change your guess)</sub>
              </Heading>
            )}
            <FormControl
              sx={styles.inputWrapper}
              isInvalid={guess.includes('!')}
            >
              <FormLabel>
                <Box pos="relative" mt="10px">
                  <InputGroup>
                    <Input
                      autoComplete="off"
                      type={
                        ['numberCurve', 'percentageCurve', 'order'].includes(
                          promptType
                        )
                          ? 'number'
                          : 'text'
                      }
                      value={guess ?? ''}
                      onChange={(event) => {
                        setGuess(event.target.value)
                      }}
                    />
                  </InputGroup>
                  <div className="bend" />
                </Box>
              </FormLabel>
              <FormHelperText>
                {['numberCurve', 'percentageCurve', 'order'].includes(
                  promptType
                )
                  ? 'Enter a number'
                  : 'Enter your guess'}
              </FormHelperText>
            </FormControl>
            <Box>
              <Button
                w="100%"
                colorScheme="teal"
                isDisabled={guess === ''}
                _disabled={{ bg: 'gray.300' }}
                onClick={() => {
                  if (guess !== '') {
                    sendGuess()
                  }
                }}
                zIndex="10"
              >
                Send Guess
              </Button>
            </Box>
          </>
        )}
      </Flex>
      {/* End Guess */}
      {/* END */}
      <Flex
        pos="relative"
        alignItems="center"
        flexDir="column"
        justifyContent="space-evenly"
        h="100%"
        display={phase === PAStates.END ? 'flex' : 'none'}
      >
        <Heading
          fontWeight={800}
          letterSpacing={'-0.05em'}
          textAlign="center"
          mb="10px"
          fontSize="24px"
        >
          Game Over
          <br />
          Your Final Score: {yourScore}
        </Heading>
        <Box>
          <Button
            w="100%"
            colorScheme="teal"
            zIndex="10"
            onClick={() => {
              window.location.href = `/join?name=${playerName}`
            }}
          >
            Join Next Game
          </Button>
        </Box>
        {creator !== undefined ? (
          <Link target="_blank" href={creator?.qrCode}>
            <Flex direction="row" align="center" gap="5px">
              <Img
                src={creator?.channelIcon}
                w="50vw"
                h="50vw"
                maxW="75px"
                maxH="75px"
                borderRadius="50px"
                boxShadow={'0px 0px 3px 2px yellow.400'}
                alt={creator?.name}
                mb="10px"
              />
              <Text
                display="inline-block"
                w="auto"
                mx="0"
                fontSize="22px"
                sx={{
                  textWrap: 'balance'
                }}
              >
                Click Here to Learn More About&nbsp;{creator?.name}
              </Text>
            </Flex>
          </Link>
        ) : null}
      </Flex>
      {/* End Guess */}
    </Flex>
  )
}

export default PlayAlong
